import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Link, Navigate, Outlet, useParams } from 'react-router';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { TopNav } from '../TopNav';
import { Sidebar } from '../Sidebar';
import { MeshBackground } from '../MeshBackground';
import { AppFooter } from '../AppFooter';
import { findProduct } from '../nav/productCatalog';
/**
 * Product-mode shell — rendered when the user is inside a specific
 * product at `/products/:slug/*`. Differences from PlatformShell:
 *
 *  1. Sidebar sections come from `productCatalog[slug].sidebar` — scoped
 *     to just that product's features (Dashboard, Domains, Alert Rules,
 *     Templates, Delivery Log, Reports).
 *  2. A "← Back to One" chip renders above the first section so the
 *     user can always bail back to /dashboard without hunting the top
 *     nav.
 *  3. Sidebar accent colour swaps to the product's accent (indigo for
 *     SSL, emerald for DNS, amber for Domain Expiry, etc.) so the user
 *     feels at a glance which product they're in.
 *
 * Unknown product slugs redirect to /my-products rather than 404 — the
 * catalog is the source of truth for what exists.
 */
export function ProductShell({ slug: slugProp } = {}) {
    const params = useParams();
    const slug = slugProp ?? params.slug ?? '';
    const product = findProduct(slug);
    if (!product) {
        return _jsx(Navigate, { to: "/my-products", replace: true });
    }
    // Coming-soon products shouldn't be enterable — bounce to My Products
    // so the user sees the trial CTA on the tile instead of a half-built
    // inner page.
    if (product.availability === 'COMING_SOON') {
        return _jsx(Navigate, { to: "/my-products", replace: true });
    }
    const sections = product.sidebar.map((s) => ({
        title: s.title,
        items: s.items.map((i) => ({
            label: i.label,
            to: `/products/${product.slug}/${i.path}`,
            Icon: i.Icon,
        })),
    }));
    const accent = `var(${product.accent.var})`;
    const accentSoft = product.accent.softVar
        ? `var(${product.accent.softVar})`
        : 'var(--lav-50)';
    return (_jsxs(_Fragment, { children: [_jsx(MeshBackground, {}), _jsxs("div", { className: "relative z-[1] grid min-h-screen mx-auto max-w-[1680px]", style: {
                    gridTemplateColumns: '272px 1fr',
                    gridTemplateRows: '68px 1fr',
                    gridTemplateAreas: `"nav nav" "side main"`,
                }, children: [_jsx(TopNav, {}), _jsx(Sidebar, { sections: sections, accentColor: accent, accentSoftColor: accentSoft, accentBorderColor: accent, header: _jsx(ProductSidebarHeader, { product: product, accent: accent }) }), _jsxs("main", { className: "flex flex-col overflow-y-auto px-9 pt-6 pb-10", style: { gridArea: 'main' }, children: [_jsx("div", { className: "flex-1", children: _jsx(Outlet, {}) }), _jsx(AppFooter, {})] })] })] }));
}
function ProductSidebarHeader({ product, accent, }) {
    const { shortName, Icon } = product;
    return (_jsxs("div", { className: "flex flex-col gap-3 px-[4px]", children: [_jsxs(Link, { to: "/", className: "group inline-flex items-center gap-2 self-start rounded-full border border-[var(--line)] bg-[var(--glass-bg-strong)] px-3 py-[6px] text-[12px] font-medium text-[var(--ink-700)] no-underline shadow-[var(--shadow-xs)] transition-[transform,box-shadow,color] hover:-translate-y-px hover:text-[var(--ink-900)] hover:shadow-[var(--shadow-sm)]", "aria-label": "Back to main dashboard", children: [_jsx(ArrowLeftIcon, { className: "h-[13px] w-[13px]", "aria-hidden": true }), _jsx("span", { children: "Back to One" })] }), _jsxs("div", { className: "flex items-center gap-2.5 px-[10px]", children: [_jsx("span", { className: "grid h-[28px] w-[28px] place-items-center rounded-[8px] text-white", style: {
                            background: accent,
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 4px 12px -4px rgba(11,13,35,0.25)',
                        }, "aria-hidden": true, children: _jsx(Icon, { className: "h-[15px] w-[15px]" }) }), _jsxs("div", { className: "flex flex-col leading-tight", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink-500)]", style: { fontFamily: 'var(--f-mono)' }, children: "Product" }), _jsx("span", { className: "text-[14px] font-semibold text-[var(--ink-900)]", children: shortName })] })] })] }));
}
